import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { qk } from '@/api/queries';
import type { AiValues, SecretState, Section } from '@/api/types';
import { Button, Drawer, FieldHint, Input, Segmented, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { patchSection } from './api';
import { SecretField } from './SecretField';
import s from './settings.module.css';

type SecretName = 'api_key' | 'claude_code_token';

/**
 * The AI provider form (P1-F04, additive): adapter, model, effort, the per-organization budget, the
 * Design Library's monthly AI budget (PLAN D28) and the parallel jobs. The adapter (P2-A07; TZ §8.3) picks the Claude API (the API key), Claude Code on a
 * Claude subscription (a token from `claude setup-token`), or the API with Claude Code when no key is
 * set. Both secrets are write-only: the form shows {set, masked} from the server, sends a new value
 * only when one is typed (or null to remove it), and clears the field after saving.
 */
export function AiEditDrawer({ data, onClose }: { data: Section<AiValues>; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const qc = useQueryClient();
  const v = data.values;
  const [adapter, setAdapter] = useState(v.adapter);
  const [model, setModel] = useState(v.model);
  const [effort, setEffort] = useState(v.effort);
  const [budget, setBudget] = useState(String(v.org_monthly_budget_usd));
  const [libraryBudget, setLibraryBudget] = useState(String(v.library_monthly_budget_usd));
  const [parallel, setParallel] = useState(String(v.parallel_jobs));
  const [typed, setTyped] = useState<Record<SecretName, string>>({ api_key: '', claude_code_token: '' });
  const [clearing, setClearing] = useState<Record<SecretName, boolean>>({ api_key: false, claude_code_token: false });
  const [problem, setProblem] = useState<string | null>(null);
  const secrets = data.secrets as Partial<Record<SecretName, SecretState>>;
  const usesApi = adapter !== 'claude_code';
  const usesCode = adapter !== 'claude_api';

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (adapter !== v.adapter) body.adapter = adapter;
      if (model.trim() !== v.model) body.model = model.trim();
      if (effort !== v.effort) body.effort = effort;
      if (Number(budget) !== v.org_monthly_budget_usd) body.org_monthly_budget_usd = Number(budget);
      if (Number(libraryBudget) !== v.library_monthly_budget_usd) body.library_monthly_budget_usd = Number(libraryBudget);
      if (Number(parallel) !== v.parallel_jobs) body.parallel_jobs = Number(parallel);
      for (const name of ['api_key', 'claude_code_token'] as const) {
        if (typed[name].trim()) body[name] = typed[name].trim();
        else if (clearing[name]) body[name] = null;
      }
      return patchSection<AiValues>('ai', body);
    },
    onSuccess: (next) => {
      // The response carries only {set, masked}: the typed secrets are dropped from memory with the form.
      setTyped({ api_key: '', claude_code_token: '' });
      qc.setQueryData(qk.settings('ai'), next);
      toast(t('ai_edit.saved'));
      onClose();
    },
    onError: (err) => setProblem(errorText(err, tc)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    save.mutate();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={t('ai_edit.title')}
      sub={t('ai_edit.sub')}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={38} type="submit" form="ai-settings" disabled={save.isPending}>
            {t('ai_edit.save')}
          </Button>
        </div>
      }
    >
      <form id="ai-settings" className={s.drawerForm} onSubmit={submit} noValidate>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('ai_edit.adapter')}</span>
          <Segmented
            variant="form"
            label={t('ai_edit.adapter')}
            value={adapter}
            onChange={setAdapter}
            className={s.alignStart}
            options={(['claude_api', 'claude_code', 'claude_api_and_code'] as const).map((k) => ({ value: k, label: t(`ai.adapters.${k}`) }))}
          />
        </div>
        <Input label={t('ai_edit.model')} value={model} onChange={(e) => setModel(e.target.value)} mono />
        <div className={s.group}>
          <span className={s.groupLabel}>{t('ai_edit.effort')}</span>
          <Segmented
            variant="form"
            label={t('ai_edit.effort')}
            value={effort}
            onChange={setEffort}
            className={s.alignStart}
            options={(['low', 'medium', 'high', 'xhigh', 'max'] as const).map((k) => ({ value: k, label: t(`ai_edit.efforts.${k}`) }))}
          />
        </div>
        <div className={s.grid2}>
          <Input label={t('ai_edit.budget')} inputMode="numeric" value={budget} onChange={(e) => setBudget(e.target.value)} />
          <Input label={t('ai_edit.parallel')} inputMode="numeric" value={parallel} onChange={(e) => setParallel(e.target.value)} />
        </div>
        <Input label={t('ai_edit.library_budget')} inputMode="numeric" value={libraryBudget} onChange={(e) => setLibraryBudget(e.target.value)} />
        <FieldHint>{t('ai_edit.library_budget_hint')}</FieldHint>
        {(['api_key', 'claude_code_token'] as const)
          .filter((name) => (name === 'api_key' ? usesApi : usesCode))
          .map((name) => {
            const k = name === 'api_key' ? 'key' : 'code_token';
            return (
              <SecretField
                key={name}
                testId={name === 'api_key' ? 'ai-key-state' : 'ai-code-token-state'}
                labels={{
                  label: t(`ai_edit.${k}`),
                  set: (masked) => t('ai_edit.key_set', { masked }),
                  unset: t('ai_edit.key_unset'),
                  replace: t(`ai_edit.${k}_new`),
                  clear: t(`ai_edit.${k}_clear`),
                  willClear: t(`ai_edit.${k}_will_clear`),
                  hint: t(`ai_edit.${k}_hint`),
                }}
                state={secrets[name]}
                value={typed[name]}
                clearing={clearing[name]}
                onType={(value) => {
                  setTyped((x) => ({ ...x, [name]: value }));
                  setClearing((x) => ({ ...x, [name]: false }));
                }}
                onClear={() => setClearing((x) => ({ ...x, [name]: !x[name] }))}
              />
            );
          })}
      </form>
    </Drawer>
  );
}
